import { createSelector, createSlice, PayloadAction } from "@reduxjs/toolkit";

import { type RootState } from "app";

export type ToolbarTab = "general" | "tools";

interface BottomToolbarState {
    /** Currently selected tab */
    activeTab: ToolbarTab;
    /** Whether the extension panel is visible */
    isExpanded: boolean;
    /** Currently active tool ID, or null if no tool is active */
    activeTool: string | null;
}

const initialState: BottomToolbarState = {
    activeTab: "general",
    isExpanded: false,
    activeTool: null,
};

export const bottomToolbarSlice = createSlice({
    name: "bottomToolbar",
    initialState,
    reducers: {
        /**
         * Toggle a tab:
         * - If collapsed → expand and set to this tab
         * - If expanded and same tab → collapse
         * - If expanded and different tab → collapse first (to animate out), then switch tab
         *   (The component will handle re-expanding after animation completes)
         */
        toggleTab: (state, action: PayloadAction<ToolbarTab>) => {
            const tab = action.payload;

            if (!state.isExpanded) {
                // Currently collapsed, expand and set tab
                state.isExpanded = true;
                state.activeTab = tab;
            } else if (state.activeTab === tab) {
                // Same tab clicked, collapse
                state.isExpanded = false;
            } else {
                // Different tab clicked - collapse first to animate out, then switch tab
                state.isExpanded = false;
                state.activeTab = tab;
            }
        },

        /**
         * Set the active tab directly
         */
        setActiveTab: (state, action: PayloadAction<ToolbarTab>) => {
            state.activeTab = action.payload;
        },

        /**
         * Expand the toolbar extension
         */
        expand: (state) => {
            state.isExpanded = true;
        },

        /**
         * Collapse the toolbar extension
         */
        collapse: (state) => {
            state.isExpanded = false;
        },

        /**
         * Set the active tool (when a tool is activated from Tools tab)
         * This also forces the toolbar to be expanded
         */
        setActiveTool: (state, action: PayloadAction<string | null>) => {
            state.activeTool = action.payload;
            if (action.payload !== null) {
                // When a tool is activated, ensure toolbar is expanded
                state.isExpanded = true;
            }
        },

        /**
         * Save and exit the current tool (keeps changes)
         */
        saveTool: (state) => {
            state.activeTool = null;
            // Optionally collapse after exiting tool
            // state.isExpanded = false;
        },

        /**
         * Exit the current tool (discards changes)
         */
        exitTool: (state) => {
            state.activeTool = null;
            // Optionally collapse after exiting tool
            // state.isExpanded = false;
        },

        /**
         * Reset to initial state
         */
        reset: () => initialState,
    },
});

export const bottomToolbarActions = bottomToolbarSlice.actions;
export const bottomToolbarReducer = bottomToolbarSlice.reducer;

// Selectors
const selectBottomToolbarState = (state: RootState) => state.bottomToolbar;

export const selectActiveTab = createSelector(selectBottomToolbarState, (state) => state.activeTab);

export const selectIsExpanded = createSelector(selectBottomToolbarState, (state) => state.isExpanded);

export const selectActiveTool = createSelector(selectBottomToolbarState, (state) => state.activeTool);

export const selectIsToolActive = createSelector(selectBottomToolbarState, (state) => state.activeTool !== null);

/**
 * When a tool is active, the tabs should show Save/Exit instead of General/Tools
 */
export const selectShowSaveExit = selectIsToolActive;
